/**
 * NavigationContext - Smooth Mobile Navigation Stack
 *
 * Provides navigation state management with smooth transitions,
 * gesture support, and auth integration for mobile-first UX.
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackPageView } from '../utils/metaPixel';

const NavigationContext = createContext();

export function useNavigationStack() {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigationStack must be used within NavigationProvider');
  }
  return context;
}

export function NavigationProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [navigationStack, setNavigationStack] = useState([]);
  const [transitionState, setTransitionState] = useState({
    direction: 'forward', // 'forward' | 'back' | 'modal' | 'none'
    isAnimating: false,
    depth: 0
  });
  const [modalStack, setModalStack] = useState([]);
  const previousLocationRef = useRef(location.pathname);

  // Track navigation depth based on history
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousLocationRef.current;

    if (currentPath !== previousPath) {
      // Log navigation event (analytics hook point)
      console.log('[Navigation]', {
        from: previousPath,
        to: currentPath,
        timestamp: new Date().toISOString()
      });

      // Track page view with Meta Pixel
      trackPageView();

      // Update stack
      setNavigationStack(prev => {
        const newStack = [...prev, {
          pathname: currentPath,
          timestamp: Date.now(),
          state: location.state
        }];
        // Keep last 50 entries
        return newStack.slice(-50);
      });

      previousLocationRef.current = currentPath;
    }
  }, [location]);

  /**
   * Navigate with smooth transition animation
   */
  const navigateWithTransition = useCallback((to, options = {}) => {
    const {
      direction = 'forward',
      replace = false,
      state = {},
      transition = 'slide'
    } = options;

    // Set transition state
    setTransitionState({
      direction,
      isAnimating: true,
      depth: navigationStack.length + (replace ? 0 : 1)
    });

    // Perform navigation
    navigate(to, {
      replace,
      state: {
        ...state,
        _transition: { direction, transition }
      }
    });

    // Clear animation flag after transition completes
    setTimeout(() => {
      setTransitionState(prev => ({ ...prev, isAnimating: false }));
    }, 400);
  }, [navigate, navigationStack.length]);

  /**
   * Go back with animation
   */
  const goBackWithAnimation = useCallback(() => {
    setTransitionState(prev => ({
      ...prev,
      direction: 'back',
      isAnimating: true
    }));

    navigate(-1);

    setTimeout(() => {
      setTransitionState(prev => ({ ...prev, isAnimating: false }));
    }, 400);
  }, [navigate]);

  /**
   * Open modal overlay
   */
  const openModal = useCallback((component, options = {}) => {
    const modalId = `modal-${Date.now()}`;
    setModalStack(prev => [...prev, {
      id: modalId,
      component,
      options
    }]);

    console.log('[Navigation] Modal opened:', modalId);
    return modalId;
  }, []);

  /**
   * Close modal
   */
  const closeModal = useCallback((modalId) => {
    setModalStack(prev => {
      if (modalId) {
        return prev.filter(m => m.id !== modalId);
      }
      // Close top modal if no ID provided
      return prev.slice(0, -1);
    });

    console.log('[Navigation] Modal closed:', modalId);
  }, []);

  /**
   * Clear all modals
   */
  const closeAllModals = useCallback(() => {
    setModalStack([]);
    console.log('[Navigation] All modals closed');
  }, []);

  /**
   * Check if can go back
   */
  const canGoBack = navigationStack.length > 1;

  /**
   * Get current transition metadata
   */
  const getTransitionMetadata = useCallback(() => {
    return location.state?._transition || { direction: 'forward', transition: 'slide' };
  }, [location.state]);

  /**
   * Navigate to home with reset
   */
  const navigateToHome = useCallback(() => {
    setNavigationStack([]);
    navigateWithTransition('/', { replace: true, direction: 'back' });
  }, [navigateWithTransition]);

  const value = {
    // Navigation methods
    navigateWithTransition,
    goBackWithAnimation,
    navigateToHome,

    // Modal methods
    openModal,
    closeModal,
    closeAllModals,

    // State
    transitionState,
    navigationStack,
    modalStack,
    canGoBack,
    currentPath: location.pathname,

    // Utilities
    getTransitionMetadata
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}


